import { useEffect, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Globe, Award, Users, Zap, CheckCircle2, Trophy, Target } from 'lucide-react';
import CMYKScene from '@/components/3d/CMYKScene';
import { Link } from 'react-router-dom';

const HomePage = () => {
  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.8]);

  useEffect(() => {
    document.title = 'Multicolor International | Premium Printing, Packaging & Branding Solutions';
  }, []);

  const services = [
    { icon: '🖨️', title: 'Screen/Offset Printing', description: 'High-quality commercial printing solutions for all your business needs' },
    { icon: '🏷️', title: 'Crystal/Coated Labels', description: 'Premium labels with superior finish and durability' },
    { icon: '📋', title: 'Sticker/Vinyl/Mylar', description: 'Custom stickers and vinyl printing for branding and promotions' },
    { icon: '💳', title: 'Plastic Photo ID Cards', description: 'Professional ID cards for staff, students, and organizations' },
    { icon: '⚙️', title: 'Industrial Name Plates', description: 'Durable industrial labeling solutions' },
    { icon: '📺', title: 'Media In/Out Advertising', description: 'Indoor and outdoor advertising solutions' },
    { icon: '👕', title: 'T-Shirts/Caps Printing', description: 'Custom apparel printing for branding and events' },
    { icon: '☕', title: 'Mugs/Pens/Wall Clocks', description: 'Corporate merchandise and promotional products' },
    { icon: '🔐', title: 'Hologram Seals', description: 'Security hologram seals for authentication' },
    { icon: '🏅', title: 'Metallic Mounts/Coat Pins', description: 'Premium metallic products for corporate gifting' },
    { icon: '📊', title: 'Barcode Making/Printing', description: 'Complete barcode generation and printing services' },
    { icon: '🎁', title: 'Corporate Giveaways', description: 'Customized promotional items and corporate gifts' }
  ];

  const whyChooseUs = [
    { icon: Globe, title: 'International Operations', description: 'Offices in Pakistan, USA, and Canada' },
    { icon: Award, title: '70+ Years of Excellence', description: 'Trusted since 1952 with proven track record' },
    { icon: Users, title: 'Industry Leaders Trust Us', description: 'Serving Fortune 500 companies and top brands' },
    { icon: Zap, title: 'Fast Production', description: 'Quick turnaround without compromising quality' },
    { icon: CheckCircle2, title: 'Premium Quality Control', description: '100% on-time, error-free delivery' },
    { icon: Trophy, title: 'Award-Winning', description: '7 international awards including 3 Gold medals' }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-white to-gray-50">
        {/* 3D Background */}
        <div className="absolute inset-0 opacity-60" data-testid="hero-3d-canvas">
          <Canvas dpr={[1, 2]} camera={{ position: [0, 0, 10], fov: 50 }}>
            <CMYKScene />
          </Canvas>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-4xl"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="inline-block mb-6 px-4 py-2 bg-[#1B3A6D]/10 rounded-full"
            >
              <span className="text-sm font-semibold text-[#1B3A6D] tracking-wide" data-testid="hero-badge">
                TRUSTED SINCE 1952
              </span>
            </motion.div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-none mb-6" data-testid="hero-heading">
              Premium Printing,
              <br />
              <span className="gradient-text">Packaging & Branding</span>
              <br />
              Solutions
            </h1>

            <p className="text-xl text-[#5B5B5B] leading-relaxed mb-8 max-w-2xl" data-testid="hero-subheading">
              Serving leading businesses across Pakistan, USA & Canada with innovative print, packaging and promotional solutions.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" data-testid="hero-contact-btn">
                <button className="btn-primary">
                  Contact Us
                  <ArrowRight size={20} />
                </button>
              </Link>
              <Link to="/portfolio" data-testid="hero-portfolio-btn">
                <button className="btn-secondary">
                  View Portfolio
                </button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-[#111111] rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-[#111111] rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* International Presence */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="international-heading">
              International Presence
            </h2>
            <p className="text-lg text-[#5B5B5B] max-w-2xl mx-auto">
              Operating across three continents to serve you better
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { country: 'Pakistan', city: 'Lahore', address: '129-C Rehmanpura', flag: '🇵🇰' },
              { country: 'United States', city: 'Carteret, NJ', address: '11 Chrome Avenue', flag: '🇺🇸' },
              { country: 'Canada', city: 'Toronto', address: 'Coming Soon', flag: '🇨🇦' }
            ].map((location, index) => (
              <motion.div
                key={location.country}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="service-card text-center"
                data-testid={`location-${location.country.toLowerCase().replace(' ', '-')}`}
              >
                <div className="text-6xl mb-4">{location.flag}</div>
                <h3 className="text-2xl font-bold mb-2">{location.country}</h3>
                <p className="text-[#1B3A6D] font-semibold mb-2">{location.city}</p>
                <p className="text-sm text-[#5B5B5B]">{location.address}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="about-heading">
                About Multicolor International
              </h2>
              <div className="space-y-4 text-lg text-[#5B5B5B] leading-relaxed">
                <p>
                  Multicolor International is a full-service printing, branding, packaging and promotional solutions company delivering customized solutions for businesses across multiple industries.
                </p>
                <p>
                  Since 1952, we have combined manufacturing expertise, branding knowledge and production capabilities to help businesses create impactful brand experiences.
                </p>
                <p className="font-semibold text-[#1B3A6D]">
                  Led by Rtn. Mohammad Mahmood Khan, Chief Executive
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1599930113854-d6d7fd522504?w=800&q=80&auto=format"
                alt="Printing manufacturing facility"
                className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="services-heading">
              Our Services
            </h2>
            <p className="text-lg text-[#5B5B5B] max-w-2xl mx-auto">
              Comprehensive printing, packaging, and branding solutions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="service-card"
                data-testid={`service-${service.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                <p className="text-[#5B5B5B]">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="why-choose-heading">
              Why Clients Choose Us
            </h2>
            <p className="text-lg text-[#5B5B5B] max-w-2xl mx-auto">
              Delivering excellence for over seven decades
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="glass-card p-8 rounded-2xl"
                  data-testid={`why-choose-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <Icon size={40} className="text-[#1B3A6D] mb-4" />
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-[#5B5B5B]">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Awards Section */}
      <section className="py-24 bg-gradient-to-br from-[#1B3A6D] via-[#0F2547] to-[#1B3A6D] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-[#F9B233] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-64 h-64 bg-[#E31E24] rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Trophy size={64} className="mx-auto mb-6 text-[#F9B233]" />
            <h2 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="awards-heading">
              Award-Winning Excellence
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Worldwide Competition of Printing, Georgia USA, 1990
            </p>
            <div className="flex justify-center gap-8 flex-wrap">
              <div className="text-center">
                <div className="text-5xl font-bold text-[#F9B233]">3</div>
                <div className="text-sm mt-2 opacity-80">Gold Awards</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-gray-300">3</div>
                <div className="text-sm mt-2 opacity-80">Silver Awards</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#E31E24]">1</div>
                <div className="text-sm mt-2 opacity-80">Bronze Award</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="cta-heading">
              Ready to Elevate Your Brand?
            </h2>
            <p className="text-xl text-[#5B5B5B] mb-8">
              Let's discuss how we can help bring your vision to life with our premium printing and branding solutions.
            </p>
            <Link to="/contact" data-testid="cta-contact-btn">
              <button className="btn-primary text-lg px-8 py-4">
                Get In Touch
                <ArrowRight size={24} />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Brand Signature Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-96 h-96 bg-[#E31E24] rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#00A651] rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-[#F9B233] rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img
              src="/logo-transparent.png"
              alt="Multicolor International"
              className="mx-auto h-40 sm:h-56 w-auto object-contain mb-8"
              data-testid="home-bottom-logo"
            />
            <div className="max-w-2xl mx-auto">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#5B5B5B] mb-4">
                Since 1952
              </p>
              <p className="text-lg sm:text-xl text-[#5B5B5B] leading-relaxed">
                Three generations of excellence in printing, packaging, and branding.
                Trusted by industry leaders across Pakistan, USA, and Canada.
              </p>
              <div className="flex justify-center gap-2 mt-8">
                <span className="w-3 h-3 rounded-full bg-[#E31E24]" />
                <span className="w-3 h-3 rounded-full bg-[#F9B233]" />
                <span className="w-3 h-3 rounded-full bg-[#00A651]" />
                <span className="w-3 h-3 rounded-full bg-[#3FABF3]" />
                <span className="w-3 h-3 rounded-full bg-[#1B3A6D]" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;